import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/layout";
import { SEOHead } from "@/components/seo";
import { Button } from "@/components/ui/button";
import { Home } from "lucide-react";
import { useAgentBranding } from "@/hooks/useAgentBranding";
import AgentBrandingCard from "@/components/agent/AgentBrandingCard";

const PAGE_TITLE = "How to Buy a House in NJ (2026) | Step-by-Step Guide | Prime Realty Partners";
const PAGE_DESC = "Step-by-step guide to buying a house in New Jersey. From pre-approval to closing, learn everything you need to know about the NJ home buying process.";
const CANONICAL = "https://www.prpsj.com/how-to-buy-a-house-nj";

const faqs = [
  {
    q: "How much money do you need to buy a house in NJ?",
    a: "It depends on your loan type. With FHA, you need about 5.5-8.5% of the purchase price (3.5% down + closing costs). With VA or USDA, you could buy with very little out of pocket. On a $350,000 home, most buyers need $25,000-$35,000 total.",
  },
  {
    q: "How to buy a house with no money down in NJ?",
    a: "VA loans (for veterans) and USDA loans (for eligible rural areas) offer zero down payment. You can also combine low-down-payment loans with NJHMFA assistance of up to $15,000 and negotiate seller-paid closing costs to minimize your cash needed.",
  },
  {
    q: "How long does it take to buy a house in NJ?",
    a: "The typical timeline from starting your search to closing is 2-4 months. Once under contract, closing takes 30-45 days. This includes attorney review (3 days), inspection (7-10 days), and mortgage processing (3-4 weeks).",
  },
  {
    q: "How much house can I afford on a $100,000 salary in NJ?",
    a: "Generally, you can afford a home priced at $350,000-$400,000, depending on your debt, down payment, and the property taxes in the town you choose. A lender pre-approval gives you an exact number.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "How to Buy a House in New Jersey (2026)",
  description: PAGE_DESC,
  url: CANONICAL,
};

const HowToBuyHouseNJ = () => {
  useEffect(() => {
    document.title = PAGE_TITLE;
  }, []);

  const { brandedAgent, isBranded } = useAgentBranding();

  return (
    <Layout>
      <SEOHead
        title={PAGE_TITLE}
        description={PAGE_DESC}
        canonicalUrl={CANONICAL}
        schema={[faqSchema, webPageSchema]}
        keywords="how to buy a house in NJ, buying a house New Jersey, NJ home buying process, how much to buy house NJ, no money down NJ"
        noindex={isBranded}
        breadcrumbs={[{ name: "Home", url: "https://www.prpsj.com" }, { name: "How to Buy a House", url: CANONICAL }]}
      />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-primary/10 to-primary/5 py-20">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Home className="h-4 w-4" />
              Buyer's Guide
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
              How to Buy a House in New Jersey (2026)
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Your complete step-by-step guide to the NJ home buying process — from figuring out what you can afford to getting the keys.
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <article className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 space-y-16">

          {/* Section 1 */}
          <section>
            <h2 className="font-heading text-3xl font-bold mb-6">How Much Money Do You Need to Buy a House in NJ?</h2>
            <p className="text-muted-foreground mb-6">The amount you need depends on your loan type and the purchase price. Here's the reality for South Jersey:</p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-6">
              <li><strong>Down payment:</strong> 0-20% of the purchase price depending on your loan type. FHA requires 3.5%, conventional can be as low as 3%, and VA/USDA offer 0% down.</li>
              <li><strong>Closing costs:</strong> 2-5% of the purchase price. See our complete <Link to="/closing-costs-nj" className="text-primary hover:underline font-medium">NJ Closing Costs Guide</Link> for details.</li>
              <li><strong>Cash reserves:</strong> Most lenders want to see 2-3 months of mortgage payments in savings after closing.</li>
            </ul>
            <div className="bg-muted/50 rounded-lg p-6 mb-6">
              <h3 className="font-heading text-lg font-bold mb-3">Example on a $350,000 home with FHA financing:</h3>
              <ul className="space-y-1 text-muted-foreground">
                <li>Down payment (3.5%): $12,250</li>
                <li>Closing costs (3%): $10,500</li>
                <li>Cash reserves: $5,000-$7,000</li>
                <li className="font-medium text-foreground">Total needed: approximately $28,000-$30,000</li>
              </ul>
            </div>
            <p className="text-muted-foreground">
              With VA or USDA financing and seller-paid closing costs, you could potentially buy with very little out of pocket. And NJHMFA down payment assistance can provide up to $15,000 to help bridge the gap. See our{" "}
              <Link to="/first-time-home-buyer-nj" className="text-primary hover:underline font-medium">First Time Home Buyer Guide</Link> for details on assistance programs.
            </p>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="font-heading text-3xl font-bold mb-6">How to Buy a House with No Money Down</h2>
            <p className="text-muted-foreground mb-4">Buying a home with zero money down is possible in NJ through several programs.</p>
            <div className="space-y-4">
              <div>
                <h3 className="font-heading text-xl font-bold mb-2">VA Loans</h3>
                <p className="text-muted-foreground">Available to veterans, active military, and eligible surviving spouses. No down payment, no PMI, and competitive rates. This is the best zero-down option available.</p>
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold mb-2">USDA Loans</h3>
                <p className="text-muted-foreground">For eligible rural areas in NJ. Parts of Burlington County and Gloucester County qualify. No down payment required if you meet income limits.</p>
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold mb-2">Down Payment Assistance Programs</h3>
                <p className="text-muted-foreground">While not technically "no money down," programs like NJHMFA can cover your entire down payment with a forgivable loan.</p>
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold mb-2">Seller Concessions</h3>
                <p className="text-muted-foreground">In some cases, you can negotiate for the seller to cover your closing costs, reducing your out-of-pocket to just the down payment — and if you combine that with a zero-down loan, your total cash needed can be minimal.</p>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="font-heading text-3xl font-bold mb-6">How Much House Can I Afford?</h2>
            <p className="text-muted-foreground mb-4">The standard guideline is spending no more than 28-31% of your gross monthly income on housing costs (mortgage, taxes, insurance, HOA). But in NJ, property taxes make a big difference.</p>
            <p className="text-muted-foreground mb-4">A quick way to estimate: take your annual household income, multiply by 3.5-4, and that's roughly your purchase price range. So $100,000 income = approximately $350,000-$400,000 home.</p>
            <p className="text-muted-foreground mb-4">But remember — NJ property taxes can add $500-$1,500/month depending on the town. A $400,000 home in Moorestown might have $1,000/month in taxes, while the same price in Bellmawr might be $600/month. This significantly affects what you can actually afford.</p>
            <p className="text-muted-foreground">Talk to a lender to get your exact numbers. A pre-approval takes the guesswork out of it.</p>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="font-heading text-3xl font-bold mb-6">Step-by-Step Guide to Buying a House in NJ</h2>
            <div className="space-y-6">
              {[
                { title: "Step 1: Get Pre-Approved", text: "Contact a mortgage lender, provide your financial documents, and get a pre-approval letter. This tells you exactly what you can afford and makes your offer stronger." },
                { title: "Step 2: Hire a Real Estate Agent", text: "Find a local agent who knows South Jersey. Your agent works for you, helps you find homes, writes offers, and negotiates on your behalf. In NJ, the buyer's agent relationship is formalized through a buyer agency agreement." },
                { title: "Step 3: Search for Homes", text: "Your agent will set up searches based on your criteria and schedule showings. Be ready to tour homes quickly in competitive markets." },
                { title: "Step 4: Make an Offer", text: "When you find the right home, your agent will prepare an offer based on comparable sales, market conditions, and your budget. Your offer will include the purchase price, proposed closing date, contingencies (inspection, financing, appraisal), and earnest money deposit (typically 1-3% of the purchase price in South Jersey)." },
                { title: "Step 5: Attorney Review (3 Business Days)", text: "This is unique to NJ. Once the offer is accepted, both buyer and seller have 3 business days to have their attorneys review the contract. Either side can propose modifications or cancel during this period. This is standard in NJ — always have an attorney." },
                { title: "Step 6: Home Inspection", text: "Hire a licensed NJ home inspector to evaluate the property. The inspection typically costs $400-$700 and covers structure, systems, roof, plumbing, electrical, HVAC, and more. Based on findings, your attorney can negotiate repairs or credits with the seller." },
                { title: "Step 7: Mortgage Processing and Appraisal", text: "Your lender orders an appraisal to confirm the home's value supports the loan amount. During this time, your lender will finalize underwriting — avoid making large purchases, changing jobs, or opening new credit accounts." },
                { title: "Step 8: Final Walkthrough", text: "The day before or morning of closing, you'll do a final walkthrough to confirm the home's condition and any agreed-upon repairs were completed." },
                { title: "Step 9: Closing Day", text: "You'll meet at the title company or attorney's office to sign documents, pay your closing costs, and receive the keys. In NJ, closings typically take 1-2 hours. Bring a government-issued ID and a certified or cashier's check for your closing costs (your attorney will tell you the exact amount)." },
              ].map((step, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold flex-shrink-0 text-sm">
                    {i + 1}
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold mb-1">{step.title}</h3>
                    <p className="text-muted-foreground">{step.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="font-heading text-3xl font-bold mb-6">Buying a House in South Jersey — What Makes It Different</h2>
            <p className="text-muted-foreground mb-4">New Jersey has a few unique aspects to the home buying process that differ from other states.</p>
            <div className="space-y-4">
              <div>
                <h3 className="font-heading text-xl font-bold mb-2">Attorney review is standard</h3>
                <p className="text-muted-foreground">Unlike many states, NJ home purchases involve real estate attorneys for both sides. This protects you during the contract phase.</p>
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold mb-2">Property taxes are high but vary wildly</h3>
                <p className="text-muted-foreground">NJ has some of the highest property taxes in the nation, but rates differ dramatically by town. Always factor taxes into your budget — not just the purchase price.</p>
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold mb-2">Flood zones matter</h3>
                <p className="text-muted-foreground">Parts of South Jersey, especially along the Delaware River and in low-lying areas, fall in FEMA flood zones. Flood insurance can add significant cost. Your agent should flag this early.</p>
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold mb-2">Transfer taxes</h3>
                <p className="text-muted-foreground">NJ charges a Realty Transfer Fee on property sales, typically paid by the seller. Buyers pay a minimal mansion tax on homes over $1 million.</p>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section>
            <h2 className="font-heading text-3xl font-bold mb-6">Frequently Asked Questions</h2>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <details key={i} className="group border border-border rounded-lg">
                  <summary className="flex items-center justify-between cursor-pointer p-4 font-medium text-foreground hover:bg-muted/50 rounded-lg">
                    {faq.q}
                    <span className="ml-2 transition-transform group-open:rotate-180">▼</span>
                  </summary>
                  <div className="px-4 pb-4 text-muted-foreground">{faq.a}</div>
                </details>
              ))}
            </div>
          </section>

          {/* Explore Communities */}
          <section>
            <h2 className="font-heading text-3xl font-bold mb-4">Explore South Jersey Communities</h2>
            <p className="text-muted-foreground mb-6">Ready to start your search? Browse homes for sale across South Jersey's most popular communities.</p>
            <div className="flex flex-wrap gap-2">
              {[
                { name: "Cherry Hill", path: "/cherry-hill-homes-for-sale" },
                { name: "Moorestown", path: "/moorestown-homes-for-sale" },
                { name: "Mount Laurel", path: "/mount-laurel-homes-for-sale" },
                { name: "Marlton", path: "/marlton-homes-for-sale" },
                { name: "Voorhees", path: "/voorhees-homes-for-sale" },
                { name: "Haddonfield", path: "/haddonfield-homes-for-sale" },
                { name: "Washington Township", path: "/washington-township-homes-for-sale" },
                { name: "Gloucester Township", path: "/gloucester-township-homes-for-sale" },
                { name: "Deptford", path: "/deptford-homes-for-sale" },
                { name: "Cinnaminson", path: "/cinnaminson-homes-for-sale" },
                { name: "Medford", path: "/medford-homes-for-sale" },
                { name: "Collingswood", path: "/collingswood-homes-for-sale" },
              ].map((c) => (
                <Link key={c.name} to={c.path} className="inline-flex items-center px-3 py-1.5 rounded-full border border-border text-sm text-primary hover:bg-primary/5 transition-colors">
                  {c.name}
                </Link>
              ))}
            </div>
          </section>

          {/* Agent Branding */}
          {isBranded && brandedAgent && (
            <section>
              <AgentBrandingCard agent={brandedAgent} />
            </section>
          )}

          {/* CTA */}
          <section className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl p-8 md:p-12 text-center">
            <h2 className="font-heading text-3xl font-bold mb-4">Ready to Start Your Home Search in South Jersey?</h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              {isBranded && brandedAgent
                ? `${brandedAgent.display_name || "Your agent"} knows every town in Camden, Burlington, and Gloucester counties.`
                : "Our agents know every town in Camden, Burlington, and Gloucester counties. Let us help you find the right home at the right price."}
            </p>
            <Button asChild size="lg">
              <Link to={isBranded && brandedAgent?.slug ? `/agent/${brandedAgent.slug}` : "/contact"}>
                {isBranded && brandedAgent ? `Contact ${brandedAgent.display_name?.split(" ")[0] || "Agent"}` : "Contact an Agent"}
              </Link>
            </Button>
          </section>
        </div>
      </article>
    </Layout>
  );
};

export default HowToBuyHouseNJ;
